import { Security } from '@okta/okta-react'
import { PropsWithChildren, createContext, useMemo } from 'react'
import { Loading } from '../components/Loading'
import OktaAuth from '@okta/okta-auth-js'
import { RestoreOriginalUriFunction } from '@okta/okta-react/bundles/types/OktaContext'
import { SpaConfig, useGetSpaConfig } from '../hooks/useGetSpaConfig'

type ConfigContextValue = {
  config: SpaConfig['config']
  monitorConfig: SpaConfig['monitorConfig']
  ui: SpaConfig['ui']
  school: SpaConfig['school']
}

export const ConfigContext = createContext<ConfigContextValue>({
  config: undefined,
  monitorConfig: undefined,
  ui: null,
  school: { name: '' },
})

export function ConfigProvider(props: Readonly<PropsWithChildren>) {
  const { config, monitorConfig, ui, school, isLoading } = useGetSpaConfig()

  const spaConfigContextValue = useMemo(
    () => ({ config, monitorConfig, ui, school }),
    [config, monitorConfig, school, ui],
  )

  if (isLoading) {
    return <Loading />
  }

  const isOktaEnabled = !!config

  if (!isOktaEnabled) {
    return <>{props.children}</>
  }

  const restoreOriginalUri: RestoreOriginalUriFunction = async (
    _oktaAuth: OktaAuth,
    originalUri: string,
  ) => {
    window.location.replace(originalUri || '/')
  }

  const oktaAuthInit = new OktaAuth({
    issuer: config?.issuer || '',
    clientId: config?.clientId || '',
    redirectUri: window.location.origin + '/login/callback',
  })

  return (
    <Security oktaAuth={oktaAuthInit} restoreOriginalUri={restoreOriginalUri}>
      <ConfigContext.Provider value={spaConfigContextValue}>
        {props.children}
      </ConfigContext.Provider>
    </Security>
  )
}
